<template>
  <div id="map" class="map-container">
    <TrailDataCard
      floating-layout
      :hide-elevation="showHikeElevation || showActivityPanel || (showActivitySheet && !isActive)"
      :sheet-panel-snap="showActivitySheet && !isActive ? activitySheetSnap : null"
    >
      <template v-if="recording" #sidebar-body>
        <div v-if="hikeGeojson" class="rm-tabs" role="tablist">
          <button
            v-for="tab in sidebarTabs"
            :key="tab.key"
            type="button"
            role="tab"
            class="rm-tab"
            :class="{ active: activeTab === tab.key }"
            :style="{ '--rm-tab-color': tab.color }"
            :aria-selected="activeTab === tab.key"
            @click="setTab(tab.key)"
          >
            <span class="rm-dot" :style="{ background: tab.color }"></span>
            <span class="rm-tab__label">{{ tab.label }}</span>
          </button>
        </div>

        <SidebarStatsGrid v-if="activeTab === 'recording' || !hikeGeojson" :items="recordingStats" />
        <template v-else>
          <SidebarStatsGrid :items="hikeStats" />
          <div class="rm-linked-hike">
            <span class="rm-dot" :style="{ background: hikeColor }"></span>
            <span class="rm-linked-hike__text">{{ $t('recording_map.linked_hike') }}</span>
            <a v-if="connectedHike.shareableUrl" class="rm-linked-hike__link" :href="connectedHike.shareableUrl">
              {{ $t('recording_map.view_hike') }}
            </a>
          </div>
          <!-- The hike map's own day/route panel, so a multi-day hike reads exactly as it
               does on its own page. -->
          <HikeTrailsPanel
            v-if="connectedHike.plannedTrails.length > 1 && !isActive"
            class="recording-hike-panel"
            :hike-name="hikePanelTitle"
            :planned-trails="connectedHike.plannedTrails"
            :hike-days="connectedHike.hikeDays"
            :collapsed="hikePanelCollapsed"
            :active-view-prop="hikeView"
            :show-actions="false"
            @update:collapsed="hikePanelCollapsed = $event"
            @view-changed="onHikeViewChanged"
          />
        </template>

        <label v-if="hikeGeojson" class="rm-toggle">
          <span class="rm-dot" :style="{ background: otherTab.color }"></span>
          <span class="rm-toggle__label">{{ toggleLabel }}</span>
          <input v-model="showOther" class="rm-toggle__input" type="checkbox" role="switch" @change="onShowOtherChanged" />
          <span class="rm-toggle__switch" aria-hidden="true"></span>
        </label>
      </template>
    </TrailDataCard>
    <!-- On the hike's tab the elevation follows the hike, using the hike map's own
         stage-aware profile in place of the recording's. -->
    <HikeMapElevationProfile
      v-if="showHikeElevation && !isActive"
      class="recording-hike-elevation"
      :geojson="hikeGeojson"
      :active-view="hikeView"
      :planned-trails="connectedHike.plannedTrails"
      :hike-days="connectedHike.hikeDays"
      @view-changed="onHikeViewChanged"
    />
    <RecordingActivityPanel
      v-if="showActivityPanel && !isActive"
      class="recording-activity-panel"
      :input="activityInput"
      :coordinates="recordingCoordinates"
      :get-map="getMapForMarker"
    />
    <RecordingActivityPanel
      v-if="showActivitySheet && !isActive"
      sheet
      class="recording-activity-sheet"
      :input="activityInput"
      :coordinates="recordingCoordinates"
      :get-map="getMapForMarker"
      @snap-change="activitySheetSnap = $event"
    />
    <MapControls/>
    <!-- The pill row with the "On this map" control, as on /map and the hike map. -->
    <OnThisMapButtons :button-clicked="buttonTapped" :buttons="buttons" :maps-button-id="mapsButtonId(buttons)"/>
    <BaseMapStylesDialog />
    <EditRecordingDetails />
    <LoadingDialog :show-loading-dialog="loading" />
    <MapDataPOIDetailDialog/>
    <GoToLocationDialog />
    <WaypointDialog />
    <WaypointAddingNotification />
    <MapContextMenu show-measure />
    <MeasureTool
      v-if="measureTool.open"
      context="recording"
      :route="measureRoute"
      :start-at="measureTool.startAt"
      @close="closeMeasureTool"
    />
    <MapOptionsDialog
        @edit-details="editRecording"
        @edit="editRecordingInPlanner"
        @waypoints="openWaypoints"
        @goto="showGoTo"
        @flyover="showFlyover"
        @print="openPrintMap"
        @measurement="showMeasurement"
        @report-issue="reportIssue"
        @create="createNewHike"
        @download="openDownload"
        @embed="openEmbed"
        :show-edit-details="canEditRecording"
        :edit-hike="true"
        :show-create="false"
        :show-waypoints="true"
        :show-go-to="true"
        :show-flyover="true"
        :show-measurement="true"
        :show-print="true"
        :show-report-issue="false"
        :show-download="true"
        :show-embed="canEmbed"
      />
    <div class="bottom-controls-container">
      <div v-if="miniCoordsBar" class="coordsbar">
        <span class="material-icons">my_location</span> {{ miniCoordsBar }}
      </div>
      <ScaleBar v-if="mapInstance" :map="mapInstance" :latitude="mapInstance?.getCenter()?.lat || 0" />
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import BaseMapStylesDialog from '../../apps/./base_map_dialog/BaseMapStylesDialog.vue';
import OnThisMapButtons from '../map/components/on_this_map/OnThisMapButtons.vue';
import { mapsButtonId } from '../../utils/onThisMap';
import MapControls from '../../apps/components/MapControls.vue';
import EditRecordingDetails from '../../apps/edit_recording_details/EditRecordingDetails.vue';
import TrailDataCard from '../../apps/trail_data_card/TrailDataCard.vue';
import graph_client from '../../utils/graphql/graph_client';
import { GetRecordedTrailById } from '../../utils/graphql/recorded-trail-graph';
import { GetHike } from '../../utils/graphql/hikes-graph';
import { HiikerMapStyle, ThreeDimensionalMapManager } from '../../utils/models/MapStyles';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import { buildRecordingShareConfig } from '../flyover_share/recordingShareConfig';
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import { ensurePrintMapAccess, setPrintMapEntitlement } from '../../utils/print_maps/printMapAccess';
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import { Modal } from 'bootstrap'
import MapOptionsDialog from '../map_options_dialog/MapOptionsDialog.vue';
import MapDataPOIDetailDialog from "../map_data_poi_dialog/MapDataPOIDetailDialog.vue";
import ScaleBar from "../components/ScaleBar.vue";
import GoToLocationDialog from "../go_to_location_dialog/GoToLocationDialog.vue";
import WaypointDialog from "../waypoint_dialog/WaypointDialog.vue";
import WaypointAddingNotification from "../waypoint_adding_mode_notificaiton/WaypointAddingNotification.vue";
import MapContextMenu from "../map/components/MapContextMenu.vue";
import { openMapContextMenu } from "../../utils/helpers/mapContextMenu";
import { openMapDownloadModal, openMapEmbedBuilder } from "../../utils/helpers/mapShareModals";
import MeasureTool from "../measure_tool/MeasureTool.vue";
import { MEASURE_FROM_HERE_EVENT, hideMapOptionsDialog, mayOpenMeasureTool } from "../measure_tool/entry";
import HikeTrailsPanel from "../hike_map/HikeTrailsPanel.vue";
import HikeMapElevationProfile from "../hike_map/HikeMapElevationProfile.vue";
import SidebarStatsGrid from "./components/SidebarStatsGrid.vue";
import RecordingActivityPanel from "./components/RecordingActivityPanel.vue";
import { recordingActivity } from "../../utils/recordingActivity/recordingActivity";
import { getFeatureCollectionLineString } from "../../utils/getFeatureCollectionLineString";
import { PrettyTime } from "../../utils/helpers/HikeTime";
import emitter from "../../utils/bus";
import {LayerRegistry, MapLayerCategory} from "../../utils/layer-registry";
import { ROUTE_STYLES } from "../../utils/constants/route-styles";
import { mapHeightBelow, elementsAbove } from "./mapHeight";
import { getRouteStyleSettings, buildRouteStyleExpressions } from "../../utils/routeStyleSettings";
import { recordingLinePaint } from "./helpers/recordingLineStyle";
import {
  getRecordingStartEndMarkers,
  hikePlannerUrlFor,
  removeRecordingLayers,
  reportIssueUrlFor,
  transformRecordingForStyling,
} from "./helpers/recordingLayers";
import { distanceMarkersAlong, distanceMarkerLayer } from "../hike_map/helpers/distanceMarkers";
import {
  boundsForTab,
  layersVisibleFor,
  readShowOtherPreference,
  splitHikeLines,
  writeShowOtherPreference,
} from "./helpers/hikeOverlay";
import { hikeStatItems, recordingStatItems } from "./helpers/sidebarStats";
import { GridRefUtils } from "../../utils/helpers/GridRefPreference";
import { IndexedDBCache } from "../../utils/crosshair_altitude_service/indexed_db_cache";
import { getElevation } from "../../utils/crosshair_altitude_service/pixel_methods";
import { throttle } from 'lodash-es';


export default {
  name: 'RecordingMap',
  components: {
    LoadingDialog,
    EditRecordingDetails,
    BaseMapStylesDialog,
    MapOptionsDialog,
    OnThisMapButtons,
    MapControls,
    TrailDataCard,
    MapDataPOIDetailDialog,
    ScaleBar,
    GoToLocationDialog,
    WaypointDialog,
    WaypointAddingNotification,
    MapContextMenu,
    MeasureTool,
    HikeTrailsPanel,
    HikeMapElevationProfile,
    SidebarStatsGrid,
    RecordingActivityPanel
  },
  data() {
    return {
      loading: true,
      // recordings/_share_modals only configures the embed builder for a public recording.
      canEmbed: Boolean(window.embedBuilderConfig),
      miniCoordsBar: null,
      mouseMoveHandler: null,
      buttons: [
        {
          id: 0, name: 'Maps', icon: 'layers', enabled: false, dropIcon: true,
        },
        {
          id: 1, name: 'Options', icon: 'add', enabled: false, dropIcon: false,
        },
      ],
      geojson: null,
      mapInstance: null,
      recording: null,
      recordingSlug: null,
      // The hike this recording was recorded against (Recording#connected_hike), when the
      // viewer can see it. Null for most recordings, which hides the switcher entirely.
      connectedHike: null,
      hikeGeojson: null,
      hikeView: { type: 'full' },
      hikePanelCollapsed: false,
      activeTab: 'recording',
      // The sidebar's "Show … on map" switch: draws the other tab's line as well. Off by
      // default; the viewer's last choice is remembered across recordings.
      showOther: readShowOtherPreference(),
      routeStyleSettings: getRouteStyleSettings(),
      isDesktop: true,
      desktopMedia: null,
      layoutObserver: null,
      // The Activity section's readings, published on the page (RecordingActivityPresenter).
      activityInput: window.recordingActivity || null,
      activitySheetSnap: null,
      measureTool: { open: false, startAt: null },
    };
  },
  computed: {
    isActive() {
      return this.measureTool.open;
    },
    // Snap to route measures along the recorded track.
    measureRoute() {
      return this.geojson ? this.transformRecordingForMeasurement(this.geojson) : null;
    },
    layerVisibility() {
      return layersVisibleFor(this.activeTab, this.showOther, !!this.hikeGeojson);
    },
    recordingColor() {
      return recordingLinePaint(this.routeStyleSettings).color;
    },
    hikeColor() {
      return this.routeStyleSettings.main.color;
    },
    sidebarTabs() {
      return [
        { key: 'recording', label: this.$t('recording_map.my_recording'), color: this.recordingColor },
        { key: 'hike', label: this.connectedHike?.name || this.$t('recording_map.view_hike'), color: this.hikeColor },
      ];
    },
    toggleLabel() {
      return this.activeTab === 'hike'
        ? this.$t('recording_map.show_my_recording')
        : this.$t('recording_map.show_on_map_named', { name: this.connectedHike?.name || '' });
    },
    otherTab() {
      return this.sidebarTabs.find((tab) => tab.key !== this.activeTab);
    },
    statFormatters() {
      const pref = MeasurementPrefUtility.preference();
      return {
        distance: (meters) => pref.bigUnitFormatter(meters / 1000, true),
        elevation: (meters) => pref.smallUnitFormatter(meters, false),
        duration: (seconds) => new PrettyTime(seconds).prettyTime(),
        t: (key, params) => this.$t(key, params || {}),
      };
    },
    recordingStats() {
      return this.recording ? recordingStatItems(this.recording, this.statFormatters) : [];
    },
    hikeStats() {
      if (!this.connectedHike) return [];
      return hikeStatItems(
        { ...this.connectedHike, routeFeatures: this.hikeGeojson?.features },
        this.statFormatters,
      );
    },
    hikePanelTitle() {
      return (this.connectedHike?.hikeDays?.length || 0) > 1
        ? this.$t('hike_map.hike_trails_panel.hike_days')
        : this.$t('hike_map.routes');
    },
    // Desktop only: on a phone the elevation lives in the recording card's bottom sheet, which
    // stays on the recording.
    showHikeElevation() {
      return !!(this.hikeGeojson && this.activeTab === 'hike' && this.isDesktop);
    },
    hasActivity() {
      return !!(this.activityInput && recordingActivity(this.activityInput));
    },
    // Desktop only, like the hike's profile: on a phone the recording card's elevation sheet stays.
    showActivityPanel() {
      return !!(this.recording && this.hasActivity && this.activeTab === 'recording' && this.isDesktop);
    },
    // A phone: the same panel as a bottom sheet, which stays on the recording as its elevation sheet did.
    showActivitySheet() {
      return !!(this.recording && this.hasActivity && !this.isDesktop);
    },
    recordingCoordinates() {
      if (!this.geojson) return [];
      if (this.geojson.type === 'LineString') return this.geojson.coordinates;
      return getFeatureCollectionLineString(this.geojson) || [];
    },
    canEditRecording() {
      const ownerFirebaseId = this.recording?.publicUser?.firebaseUserId || this.recording?.publicUser?.firebase_user_id;
      const currentUserFirebaseId = this.getCurrentUserFirebaseId();
      if (!ownerFirebaseId || !currentUserFirebaseId) return false;
      return ownerFirebaseId === currentUserFirebaseId;
    }
  },
  async mounted() {
    mapboxgl.accessToken = 'pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w';
    const isPro = await RevenueCatWrapper.isProOrProPlus();
    setPrintMapEntitlement(isPro); // Print opens a new tab, so the gate can't wait on a fresh lookup
    const mapStyle = HiikerMapStyle.getCurrentStyle(isPro);
    window.map = new mapboxgl.Map({
      container: 'map',
      style: mapStyle.styleUrl,
      center: window.lonlat || [0, 0],
      zoom: 12,
    });
      this.mapInstance = window.map; // Store instance locally for scale bar
      window.layerRegistry = new LayerRegistry(window.map);

      // Sets up the map click that drops a waypoint in "Add waypoint" mode, as on /map and the hike map
      await this.$store.dispatch('waypoints/initialiseStore');
      // Right-click opens the same Copy Coordinate / Create Waypoint menu as /map
      window.map.on('contextmenu', this.handleContextMenu);

      // Handle resize events to ensure the map fills the screen
      window.addEventListener('resize', this.resizeMap);
      window.addEventListener(MEASURE_FROM_HERE_EVENT, this.onMeasureFromHere);
    // The window resizing isn't the only thing that moves the map's top edge: the nav and
    // breadcrumb bar above it can settle to a different height after mount (web fonts,
    // the signed-in avatar, other apps mounting), which left a blank strip under the map.
    // The body is pinned to the viewport height, so watch the elements above the map itself.
    if (typeof window.matchMedia === 'function') {
      this.desktopMedia = window.matchMedia('(min-width: 769px)');
      this.desktopMedia.addEventListener?.('change', this.updateDesktopMedia);
      this.updateDesktopMedia();
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.layoutObserver = new ResizeObserver(() => this.resizeMap());
      elementsAbove(this.$el).forEach((el) => this.layoutObserver.observe(el));
    }
    this.resizeMap();

    // Add a map on load
    window.map.on('load', async () => {
      emitter.emit('map-loaded');
      await this.fetchData();
      this.addToMap();

      // Grid ref + elevation under the cursor, as on the hike map.
      const gridRef = GridRefUtils.preference();
      const cache = new IndexedDBCache();
      const handleMouseMove = async (e) => {
        const coords = gridRef.convert(e.lngLat);
        const elevation = await getElevation(e.lngLat, e.target.getZoom(), cache);
        this.miniCoordsBar = `${coords.toString()} ${elevation}`;
      };
      this.mouseMoveHandler = throttle(handleMouseMove, 50);
      window.map.on('mousemove', this.mouseMoveHandler);
    });
    window.map.on('style.load', () => {
      emitter.emit('map-style-loaded');
      this.addToMap({ fitBounds: false });
    });

    // Some modules re-add their layers after emitting this event. Listening
    // to it ensures our trail line is restored if other handlers removed it
    // when the style changed.
    emitter.on('map-style-loaded', this.restoreLayers);
    // Re-colour the track (and the hike's routes) when the route style is customised in
    // the Maps dialog or synced down from the user's other devices.
    emitter.on('route-style-settings:changed', this.applyLineStyles);
  },
  methods: {
    mapsButtonId,
    getCurrentUserFirebaseId() {
      const user = window.user;
      return user?.firebaseUserId || user?.firebase_user_id || null;
    },
    getMapForMarker() {
      return window.map ?? null;
    },
    resizeMap() {
      const container = this.$el;
      if (container) {
        const mapTop = container.getBoundingClientRect().top + window.scrollY;
        const height = `${mapHeightBelow(window.innerHeight, mapTop)}px`;
        // Setting the same height again would re-trigger the layout observer for nothing.
        if (container.style.height === height) return;
        container.style.height = height;
      }
      // Ensure map resizes when window size changes
      if (window.map) {
        window.map.resize();
      }
    },
    async buttonTapped(button) {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      switch (button) {
        case 0:
          // Map Layers
          if (window.user) {
            const mapStylesDialog = document.getElementById("map-styles-dialog");
            const modal = new Modal(mapStylesDialog);
            modal.show();
          } else {
            window.onBoardingVue.launchFor(OnBoardingGoal.MapStyles);
          }
          break;
        case 1:
          // Options
          this.closeMobilePanels();
          const optionsEl = document.getElementById('map-options-dialog');
          if (optionsEl) Modal.getOrCreateInstance(optionsEl).show();
          break;
        default:
          break;
      }
    },
    async fetchData() {
      this.recordingSlug = window.recording_id;
      console.log('RecordingMap fetchData - window.recording_id:', window.recording_id);
      const response = await graph_client.request(GetRecordedTrailById, { slug: `${this.recordingSlug}` });
      console.log('RecordingMap fetchData - response:', response);
      const { recordingBySlug } = response;
      this.recording = recordingBySlug;
      console.log('RecordingMap fetchData - recording obfuscatedSlug:', this.recording.obfuscatedSlug);
      // Use the obfuscatedSlug for editing, not the window.recording_id
      this.recordingSlug = this.recording.obfuscatedSlug;
      const geojson = JSON.parse(this.recording.lineString);
      this.geojson = geojson;
      window.trailCardDataRecording(this.recording);
      await this.fetchConnectedHike();
    },
    async fetchConnectedHike() {
      const hikeId = this.recording?.connectedHike?.id;
      if (!hikeId) return;
      try {
        // Returns null for a hike the viewer can't see (someone else's private hike), in
        // which case the recording page just shows the recording, as before.
        const { hike } = await graph_client.request(GetHike, { id: `${hikeId}` });
        if (!hike?.geojsonMultilinestringsWithStartEndPointsString) return;
        this.connectedHike = {
          id: hike.id,
          name: hike.name,
          shareableUrl: hike.shareableUrl,
          distanceMeters: hike.distanceMeters,
          durationMinutes: hike.durationMinutes,
          elevationGainMeters: hike.elevationGainMeters,
          elevationLossMeters: hike.elevationLossMeters,
          plannedTrails: hike.hikesPlannedTrails || hike.ptv2PlannedTrails || [],
          hikeDays: hike.hikeDays || [],
        };
        this.hikeGeojson = JSON.parse(hike.geojsonMultilinestringsWithStartEndPointsString);
      } catch (error) {
        console.error('RecordingMap: could not load the connected hike', error);
      }
    },
    restoreLayers() {
      this.addToMap({ fitBounds: false });
    },
    // Keeps the route clear of the sidebar card (left) and the elevation bar (bottom) on
    // desktop; on a narrow map those are bottom sheets, so an even margin does.
    fitPadding() {
      const width = window.map?.getContainer()?.clientWidth || 0;
      const height = window.map?.getContainer()?.clientHeight || 0;
      if (!this.isDesktop || width < 900 || height < 560) return 60;
      return { top: 80, right: 60, bottom: 260, left: 420 };
    },
    setTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.addToMap();
    },
    onShowOtherChanged() {
      writeShowOtherPreference(this.showOther);
      this.addToMap({ fitBounds: false });
    },
    updateDesktopMedia() {
      this.isDesktop = this.desktopMedia ? this.desktopMedia.matches : true;
    },
    onHikeViewChanged(view) {
      this.hikeView = view || { type: 'full' };
      this.addToMap();
    },
    // Re-applies the user's route style to the track and the hike's routes in place.
    applyLineStyles() {
      const map = window.map;
      if (!map) return;
      const setPaint = (layerId, props) => {
        if (!map.getLayer(layerId)) return;
        Object.entries(props).forEach(([key, value]) => {
          try { map.setPaintProperty(layerId, key, value); } catch {}
        });
      };
      const settings = getRouteStyleSettings();
      this.routeStyleSettings = settings;
      const paint = recordingLinePaint(settings);
      setPaint('recording-line-border', {
        'line-color': paint.borderColor,
        'line-width': paint.borderWidth,
        'line-opacity': paint.opacity,
      });
      setPaint('recording-line', {
        'line-color': paint.color,
        'line-width': paint.lineWidth,
        'line-opacity': paint.opacity,
      });
      const hike = buildRouteStyleExpressions(settings);
      setPaint('recording-hike-lines-border-inactive', {
        'line-color': hike.borderColorExpr,
        'line-width': hike.inactiveBorderWidthExpr,
        'line-opacity': hike.inactiveBorderOpacityExpr,
      });
      setPaint('recording-hike-lines-inactive', {
        'line-color': hike.colorExpr,
        'line-width': hike.inactiveLineWidthExpr,
        'line-opacity': hike.inactiveLineOpacity,
      });
      setPaint('recording-hike-lines-border', {
        'line-color': hike.borderColorExpr,
        'line-width': hike.activeBorderWidthExpr,
        'line-opacity': hike.activeBorderOpacityExpr,
      });
      setPaint('recording-hike-lines', {
        'line-color': hike.colorExpr,
        'line-width': hike.activeLineWidthExpr,
        'line-opacity': hike.activeLineOpacityExpr,
      });
    },
    // The connected hike's routes, drawn under the recording with the hike map's per-route
    // styling. The selected day/route is drawn at full strength and the rest dimmed.
    addHikeToMap() {
      if (!this.hikeGeojson || !this.layerVisibility.hike) return;

      const { active, inactive } = splitHikeLines(this.hikeGeojson.features, this.hikeView);
      const style = buildRouteStyleExpressions(getRouteStyleSettings());
      const dashArray = [
        'case',
        ['==', ['get', 'routeStyle'], 'transport'],
        ['literal', ROUTE_STYLES.transport.dasharray || [1, 0]],
        ['literal', [1, 0]],
      ];
      const lineLayer = (id, sourceId, features, paint, z) => {
        if (features.length === 0) return;
        window.layerRegistry.addLayer(
          {
            id,
            type: 'line',
            source: sourceId,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { ...paint, 'line-dasharray': dashArray },
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: sourceId,
              spec: { type: 'geojson', data: { type: 'FeatureCollection', features } },
            },
            z,
          }
        );
      };

      lineLayer('recording-hike-lines-border-inactive', 'recording-hike-lines-inactive', inactive, {
        'line-color': style.borderColorExpr,
        'line-width': style.inactiveBorderWidthExpr,
        'line-opacity': style.inactiveBorderOpacityExpr,
      }, 3);
      lineLayer('recording-hike-lines-inactive', 'recording-hike-lines-inactive', inactive, {
        'line-color': style.colorExpr,
        'line-width': style.inactiveLineWidthExpr,
        'line-opacity': style.inactiveLineOpacity,
      }, 4);
      lineLayer('recording-hike-lines-border', 'recording-hike-lines-active', active, {
        'line-color': style.borderColorExpr,
        'line-width': style.activeBorderWidthExpr,
        'line-opacity': style.activeBorderOpacityExpr,
      }, 5);
      lineLayer('recording-hike-lines', 'recording-hike-lines-active', active, {
        'line-color': style.colorExpr,
        'line-width': style.activeLineWidthExpr,
        'line-opacity': style.activeLineOpacityExpr,
      }, 6);
    },
    addToMap({ fitBounds = true } = {}) {
      if (!this.geojson) {
        return;
      }

      // Remove existing layers and sources
      this.removeExistingLayers();
      this.addHikeToMap();
      const recordingVisibility = this.layerVisibility.recording ? 'visible' : 'none';

      // Transform the recording data to include routeStyle property
      const transformedGeoJson = this.transformRecordingForStyling(this.geojson);

      // Get start and end markers for the recording
      const { startMarkers, endMarkers } = this.getRecordingStartEndMarkers(this.geojson);

      // Add source using LayerRegistry
      window.layerRegistry.addSource('recording-line', {
        type: 'geojson',
        data: transformedGeoJson,
      });

      // The "Recording" entry of the user's customise-route-style settings.
      const linePaint = recordingLinePaint(getRouteStyleSettings());

      // Create dash array expression - recordings use solid lines
      const dashArrayExpression = [1, 0];

      // Add border layer first (wider line with border color)
      window.layerRegistry.addLayer(
        {
          id: 'recording-line-border',
          type: 'line',
          source: 'recording-line',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            visibility: recordingVisibility,
          },
          paint: {
            'line-color': linePaint.borderColor,
            'line-width': linePaint.borderWidth, // Wider for border
            'line-opacity': linePaint.opacity,
            'line-dasharray': dashArrayExpression,
          },
        },
        MapLayerCategory.Annotations,
        { z: 8 } // Lower z-order so border appears behind main line
      );

      // Add main line layer (thinner line with main color)
      window.layerRegistry.addLayer(
        {
          id: 'recording-line',
          type: 'line',
          source: 'recording-line',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            visibility: recordingVisibility,
          },
          paint: {
            'line-color': linePaint.color,
            'line-width': linePaint.lineWidth,
            'line-opacity': linePaint.opacity,
            'line-dasharray': dashArrayExpression,
          },
        },
        MapLayerCategory.Annotations,
        { z: 9 } // Higher z-order so main line appears on top
      );

      const distanceMarkers = distanceMarkersAlong(transformedGeoJson.features, {
        isMetric: MeasurementPrefUtility.preference().isMetric(),
      });
      if (distanceMarkers.length > 0) {
        window.layerRegistry.addLayer(
          distanceMarkerLayer('recording-distance-markers', recordingVisibility),
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'recording-distance-markers',
              spec: { type: 'geojson', data: { type: 'FeatureCollection', features: distanceMarkers } },
            },
            z: 10,
          }
        );
      }

      // Map zoom levels to icon sizes so the markers scale smoothly but cap at 0.3 after zoom 12.5.
      // Note: avoiding intermediate stops near 12 keeps Mapbox from snapping the size when crossing integer zooms.
      const markerIconSizeExpression = [
        'interpolate',
        ['linear'],
        ['zoom'],
        // zoom 0  -> size 0.12 (minimum size when zoomed out)
        0,
        0.05,
        // zoom 0  -> size 0.12 (minimum size when zoomed out)
        9,
        0.1,
        // zoom 12.5 -> size 0.3 (maximum size reached here)
        12,
        0.3,
        // maintain the cap for any higher zoom levels
        22,
        0.3
      ];

      // Add end markers before start markers so start markers can sit above
      if (endMarkers.length > 0) {
        window.layerRegistry.addLayer(
          {
            id: 'recording-markers-end',
            type: 'symbol',
            source: 'recording-markers-end',
            layout: {
              'icon-image': 'hike-control-point-end',
              'icon-size': markerIconSizeExpression,
              'icon-anchor': 'bottom', // keep marker tip fixed while scaling
              'icon-allow-overlap': true,
              visibility: recordingVisibility,
            },
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'recording-markers-end',
              spec: {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: endMarkers
                },
              },
            },
            z: 20,
          }
        );
      }

      if (startMarkers.length > 0) {
        window.layerRegistry.addLayer(
          {
            id: 'recording-markers-start',
            type: 'symbol',
            source: 'recording-markers-start',
            layout: {
              'icon-image': 'hike-control-point-start',
              'icon-size': markerIconSizeExpression,
              'icon-anchor': 'bottom', // keep marker tip fixed while scaling
              'icon-allow-overlap': true,
              visibility: recordingVisibility,
            },
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'recording-markers-start',
              spec: {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: startMarkers
                },
              },
            },
            z: 25,
          }
        );
      }

      if (fitBounds) {
        const bounds = boundsForTab(
          this.activeTab,
          this.geojson.features,
          this.hikeGeojson?.features,
          this.hikeView,
        );
        if (bounds) window.map.fitBounds(bounds, { padding: this.fitPadding() });
      }
      this.loading = false;
    },
    // Recording-specific options methods
    editRecording() {
      // Edit recording details - call the global function that loads the data
      // Use the stored recordingSlug to ensure we're using the exact same value that successfully fetched the data
      if (this.recordingSlug && typeof window.showEditRecordingModal === 'function') {
        console.log('Opening edit modal with slug:', this.recordingSlug);
        window.showEditRecordingModal(this.recordingSlug);
      } else {
        console.error('Unable to edit recording: slug not available or modal function not found', {
          slug: this.recordingSlug,
          hasFunction: typeof window.showEditRecordingModal === 'function'
        });
      }
    },

    editRecordingInPlanner() {
      // Edit recording in hike planner
      if (this.recording?.id) {
        window.location.href = `/hike-planner?trail_type=Recording&trail_id=${this.recording.id}`;
      }
    },
    openDownload() {
      if (!openMapDownloadModal()) console.warn('Download modal is not mounted on this page');
    },
    openEmbed() {
      if (!openMapEmbedBuilder()) console.warn('Embed builder is not mounted on this page');
    },
    handleContextMenu(e) {
      openMapContextMenu(window.map, e, Boolean(this.$store.state.waypoints?.waypointAddingModeEnabled));
    },
    openWaypoints() {
      const el = document.getElementById('waypoint-options-dialog');
      if (el) {
        Modal.getOrCreateInstance(el).show();
      }
      const mo = document.getElementById('map-options-dialog');
      if (mo) {
        Modal.getOrCreateInstance(mo).hide();
      }
    },
    showGoTo() {
      const el = document.getElementById('go-to-location-dialog');
      if (el) { Modal.getOrCreateInstance(el).show(); }
      const mo = document.getElementById('map-options-dialog');
      if (mo) Modal.getOrCreateInstance(mo).hide();
    },
    showFlyover() {
      // Open the interactive 3D flyover of this recording. The launcher
      // (window.showMapModalForRecordedTrail) is mounted by FlyoverPreview.vue,
      // which is rendered on the recording public map page.
      if (this.recording?.id && typeof window.showMapModalForRecordedTrail === 'function') {
        // Pass the recording's share config so the preview's "Share video" button works from here.
        // (The map page has no ".generate-3d-flyover" dataset to fall back on.) The style is added
        // by the preview itself from its own current map style.
        window.showMapModalForRecordedTrail(
          this.recording.id,
          buildRecordingShareConfig(
            this.recording,
            MeasurementPrefUtility.preference(),
            window.user?.firebase_user_id || null,
          ),
        );
      } else {
        window.alert(this.$t('recording_map.unable_to_open_flyover_recording'));
      }
    },
    async openPrintMap() {
      if (!await ensurePrintMapAccess()) return;

      // Print recording map
      if (this.recording?.id) {
        window.open(`/print-maps?data_ids[]=${this.recording.id}&data_types[]=Recording`, '_blank');
      }
    },
    // + Options → Measurement tool, or the map menu's "Measure from here" (which starts at that point).
    async showMeasurement(startAt = null) {
      hideMapOptionsDialog();
      if (!await mayOpenMeasureTool()) return;
      this.measureTool = { open: true, startAt };
    },
    onMeasureFromHere(event) {
      if (event?.detail) this.showMeasurement(event.detail);
    },
    closeMeasureTool() {
      this.measureTool = { open: false, startAt: null };
    },
    // Clears what the last addToMap drew (recording line, markers, and the hike's registry
    // layers) so the map can be redrawn. Also runs on destroy.
    removeExistingLayers() {
      removeRecordingLayers(window.map, window.layerRegistry);
    },
    transformRecordingForStyling(geojson) {
      return transformRecordingForStyling(geojson);
    },
    getRecordingStartEndMarkers(geojson) {
      return getRecordingStartEndMarkers(geojson);
    },
    closeMobilePanels() {
      // For mobile compatibility - no mobile panels in recording map currently
    },
    // The sidebar's "Create a hike" action: open the planner where the map is looking.
    createNewHike() {
      window.location.href = hikePlannerUrlFor(window.map.getCenter(), window.map.getZoom());
    },
    // The sidebar's "Report an issue" action for this recording.
    reportIssue() {
      if (!this.recording?.id) {
        window.alert(this.$t('recording_map.unable_to_report_issue_no_recording'));
        return;
      }
      const failed = () => window.alert(this.$t('hike_map.failed_to_report_issue'));
      try {
        fetch(reportIssueUrlFor(window.location.pathname), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          },
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.success) {
              window.alert(this.$t('hike_map.thank_you_for_reporting'));
            } else {
              failed();
            }
          })
          .catch((error) => {
            console.error('Error reporting issue:', error);
            failed();
          });
      } catch (error) {
        console.error('Error reporting issue:', error);
        failed();
      }
    },
    transformRecordingForMeasurement(geojson) {
      // The measurement tool expects MultiLineString features with routeStyle properties
      const transformedFeatures = [];

      if (geojson.features) {
        geojson.features.forEach((feature) => {
          if (feature.geometry.type === 'LineString') {
            // Convert LineString to MultiLineString format expected by measurement tool
            const multiLineStringFeature = {
              type: 'Feature',
              geometry: {
                type: 'MultiLineString',
                coordinates: [feature.geometry.coordinates]
              },
              properties: {
                routeStyle: 'route' // Mark as main route for measurement snapping
              }
            };
            transformedFeatures.push(multiLineStringFeature);
          }
        });
      } else if (geojson.type === 'LineString') {
        // Handle case where geojson is a single LineString
        const multiLineStringFeature = {
          type: 'Feature',
          geometry: {
            type: 'MultiLineString',
            coordinates: [geojson.coordinates]
          },
          properties: {
            routeStyle: 'route' // Mark as main route for measurement snapping
          }
        };
        transformedFeatures.push(multiLineStringFeature);
      }

      return {
        type: 'FeatureCollection',
        features: transformedFeatures
      };
    },
  },
  beforeDestroy() {
    // Clean up event listener when component is destroyed
    window.removeEventListener('resize', this.resizeMap);
    window.removeEventListener(MEASURE_FROM_HERE_EVENT, this.onMeasureFromHere);
    this.layoutObserver?.disconnect();
    this.desktopMedia?.removeEventListener?.('change', this.updateDesktopMedia);
    this.layoutObserver = null;
    emitter.off('map-style-loaded', this.restoreLayers);
    emitter.off('route-style-settings:changed', this.applyLineStyles);
    if (window.map && this.mouseMoveHandler) {
      window.map.off('mousemove', this.mouseMoveHandler);
      this.mouseMoveHandler = null;
    }

    // Clean up map resources
    if (window.map) {
      window.map.off('contextmenu', this.handleContextMenu);

      // Remove sources and layers
      this.removeExistingLayers();

      // Remove the map instance
      window.map.remove();
      window.map = null;
    }
  },
};
</script>

<style scoped>
.mapboxgl-map{
  font-family: Montserrat, Helvetica, Arial, sans-serif;
}

.map-container {
  width: 100%;
  /* Fallback until resizeMap sets the exact height (window height minus the
     header and breadcrumbs above the map) — see mapHeight.ts. */
  height: 84svh;
  position: relative;
}

@media (max-width: 768px) {
  .map-container {
    height: 90svh;
  }
}

/* The recording card's body (TrailDataCard's `sidebar-body` slot): recording/hike tabs,
   stat tiles and the "Show … on map" switch. */
.rm-tabs {
  display: flex;
  border-bottom: 1px solid rgba(172, 176, 191, 0.2);
}

.rm-tab {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 10px 10px;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.rm-tab:hover {
  color: #fff;
}

.rm-tab.active {
  color: #fff;
  border-bottom-color: var(--rm-tab-color, #ffdf46);
}

.rm-tab__label {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rm-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.rm-linked-hike {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 14px 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
}

.rm-linked-hike__text {
  flex: 1;
  min-width: 0;
}

.rm-linked-hike__link {
  flex-shrink: 0;
  color: #fff;
  font-weight: 700;
  text-decoration: underline;
}

.rm-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  border-top: 1px solid rgba(172, 176, 191, 0.2);
  border-radius: 0 0 10px 10px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
}

.rm-toggle__label {
  flex: 1;
  min-width: 0;
}

.rm-toggle__input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.rm-toggle__switch {
  flex-shrink: 0;
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.2);
  transition: background 0.2s;
}

.rm-toggle__switch::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
}

.rm-toggle__input:checked + .rm-toggle__switch {
  background: #ffdf46;
}

.rm-toggle__input:checked + .rm-toggle__switch::after {
  transform: translateX(18px);
  background: #17181d;
}

.rm-toggle__input:focus-visible + .rm-toggle__switch {
  outline: 2px solid #ffdf46;
  outline-offset: 2px;
}

/* The hike map's elevation profile, placed where the recording's elevation bar sits:
   right of the sidebar (16px gutter + 360px sidebar + 16px gutter). */
.recording-hike-elevation.elevation-profile-container {
  left: 392px;
  right: 16px;
  bottom: 40px;
  margin: 0;
  max-width: 900px;
  z-index: 1000;
}

/* Where the card's elevation bar sits on desktop, which this replaces on the recording's tab. */
.recording-activity-panel {
  position: absolute;
  left: 392px;
  right: 16px;
  bottom: 40px;
  max-width: 960px;
  z-index: 1000;
}

/* Docked to the screen's foot like elevation-panel-v2's sheet: the map area is only 90svh. */
.map-container .recording-activity-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

/* HikeTrailsPanel is a floating card at the map's top-left on the hike map; here it is
   flattened into the recording card's last section. */
.recording-hike-panel.hike-trails-panel {
  position: static;
  width: auto;
  /* Takes whatever height the card has left and scrolls its day list inside it. */
  flex: 1 1 auto;
  min-height: 160px;
  max-height: none;
  background: transparent;
  backdrop-filter: none;
  box-shadow: none;
  border-top: 1px solid rgba(172, 176, 191, 0.2);
  border-radius: 0;
}

.recording-hike-panel.hike-trails-panel.collapsed {
  max-height: none;
}

/* The stat tiles above already give the hike's totals. */
.recording-hike-panel :deep(.total-stats) {
  display: none;
}

.recording-hike-panel :deep(.panel-header) {
  background: transparent;
  border-radius: 0;
  padding: 8px 8px 8px 10px;
  min-height: 0;
}

@media (max-width: 520px){
  .map-buttons-container{
    bottom: 190px !important;
  }
}
</style>
